///
/// EarthBrowser
///
/// Volcano Data Provider
///
/// Provides volcano data from local OGC sync database with custom rendering
///
/// Created by Matt Giger on December 20, 2025
/// Copyright (c) 2025 Geodata Labs
///

import type { VolcanoProperties } from '@/earthbrowser/dataset-distribution/volcanoes/VolcanoesTable'
import type { ColumnMetadata, DataQuery, DataRow, DataStats, FilterGroup, IDataProvider, ProviderCapabilities, QueryResult } from './types'

export interface VolcanoQueryOptions {
	type?: string
	country?: string
	region?: string
	epoch?: string
	minElevation?: number
}

// Map country names to ISO 3166-1 alpha-2 codes for flag emojis
const countryToCode: Record<string, string> = {
	'United States': 'US',
	'Russia': 'RU',
	'Japan': 'JP',
	'Indonesia': 'ID',
	'Chile': 'CL',
	'Ecuador': 'EC',
	'Philippines': 'PH',
	'Mexico': 'MX',
	'Papua New Guinea': 'PG',
	'New Zealand': 'NZ',
	'Argentina': 'AR',
	'Iceland': 'IS',
	'Peru': 'PE',
	'Colombia': 'CO',
	'Nicaragua': 'NI',
	'Guatemala': 'GT',
	'Italy': 'IT',
	'Ethiopia': 'ET',
	'Kenya': 'KE',
	'Tanzania': 'TZ',
	'Cameroon': 'CM',
	'Democratic Republic of the Congo': 'CD',
	'Uganda': 'UG',
	'Vanuatu': 'VU',
	'Costa Rica': 'CR',
	'El Salvador': 'SV',
	'Canada': 'CA',
	'France': 'FR',
	'Greece': 'GR',
	'Turkey': 'TR',
	'Iran': 'IR',
	'Antarctica': 'AQ',
	'Spain': 'ES',
	'Portugal': 'PT',
	'Yemen': 'YE',
	'Saudi Arabia': 'SA',
	'Eritrea': 'ER',
	'Djibouti': 'DJ',
	'Cape Verde': 'CV',
	'Comoros': 'KM',
	'Reunion': 'RE',
	'Mayotte': 'YT',
	'Madagascar': 'MG',
	'Mauritius': 'MU',
	'Tonga': 'TO',
	'Samoa': 'WS',
	'Fiji': 'FJ',
	'Solomon Islands': 'SB',
	'Dominica': 'DM',
	'Guadeloupe': 'GP',
	'Martinique': 'MQ',
	'Montserrat': 'MS',
}

// Convert ISO country code to flag emoji
const getCountryFlag = (countryName: string): string => {
	const code = countryToCode[countryName]
	if (!code) return '🏴' // Generic flag for unknown countries

	// Convert ISO code to regional indicator symbols (flag emoji)
	const codePoints = [...code].map(char =>
		0x1F1E6 - 65 + char.charCodeAt(0)
	)
	return String.fromCodePoint(...codePoints)
}

export class VolcanoDataProvider implements IDataProvider {
	readonly id: string
	readonly name: string
	readonly type = 'custom' as const
	private queryFn: (options: VolcanoQueryOptions) => Promise<VolcanoProperties[]>
	private cachedData: VolcanoProperties[] = []
	private isInitializedFlag = false

	constructor(config: {
		id: string
		name: string
		queryFn: (options: VolcanoQueryOptions) => Promise<VolcanoProperties[]>
	}) {
		this.id = config.id
		this.name = config.name
		this.queryFn = config.queryFn
	}

	async initialize(): Promise<void> {
		if (this.isInitializedFlag) return
		// Fetch initial data
		this.cachedData = await this.queryFn({})
		this.isInitializedFlag = true
	}

	async refreshData(options?: VolcanoQueryOptions): Promise<void> {
		this.cachedData = await this.queryFn(options || {})
	}

	readonly capabilities: ProviderCapabilities = {
		supportsFiltering: true,
		supportsSorting: true,
		supportsPagination: true,
		supportsFullTextSearch: true,
		supportsRegex: true,
		supportsGeometry: true,
		supportsInsert: false,
		supportsUpdate: false,
		supportsDelete: false,
		supportsTransactions: false,
		supportsIndexing: false,
		supportsAggregation: false,
		supportsJoins: false,
		isRemote: false,
		supportsStreaming: false,
		supportsCaching: false,
	}

	private columnMetadata: ColumnMetadata[] = [
		{
			id: 'name',
			name: 'Name',
			priority: 1,
			minWidth: 160,
			type: 'string',
			nullable: false,
			render: (value: unknown) => (
				<div className="font-medium">{value as string}</div>
			)
		},
		{
			id: 'country',
			name: 'Country',
			priority: 2,
			minWidth: 130,
			type: 'string',
			nullable: false,
			render: (value: unknown) => {
				const country = value as string
				const flag = getCountryFlag(country)
				return (
					<div className="flex items-center gap-2">
						<span className="text-base">{flag}</span>
						<span className="text-white/80">{country}</span>
					</div>
				)
			}
		},
		{
			id: 'type',
			name: 'Type',
			priority: 3,
			minWidth: 140,
			type: 'string',
			nullable: false,
			render: (value: unknown) => (
				<span className="text-orange-400/80">{value as string}</span>
			)
		},
		{
			id: 'elevation_m',
			name: 'Elevation',
			priority: 4,
			minWidth: 100,
			type: 'number',
			nullable: true,
			render: (value: unknown) => {
				const elev = value as number | null
				if (!elev) return <span className="text-white/40">—</span>
				return (
					<span className="text-cyan-400/80">
						{elev.toLocaleString()} m
					</span>
				)
			}
		},
		{
			id: 'region',
			name: 'Region',
			priority: 5,
			minWidth: 150,
			type: 'string',
			nullable: false,
		render: (value: unknown) => (
			<span className="text-purple-400/80">{(value as string).replace(/ Volcanic Regions?$/, '')}</span>
		)
		},
		{
			id: 'epoch',
			name: 'Epoch',
			priority: 6,
			minWidth: 110,
			type: 'string',
			nullable: true,
			render: (value: unknown) => {
				if (!value) return <span className="text-white/40">—</span>
				const epoch = value as string
				const colorClass = epoch === 'Holocene' ? 'text-green-400' : 'text-yellow-400'
				return (
					<span className={colorClass}>
						{epoch}
					</span>
				)
			}
		},
		{
			id: 'last_eruption',
			name: 'Last Eruption',
			priority: 4,
			minWidth: 120,
			type: 'string',
			nullable: true,
			render: (value: unknown) => {
				if (!value) return <span className="text-white/40">—</span>
				return (
					<span className="text-white/80">
						{value as string}
					</span>
				)
			}
		}
	]

	async getColumns(): Promise<ColumnMetadata[]> {
		return this.columnMetadata
	}

	async getStats(): Promise<DataStats> {
		return {
			totalRows: this.cachedData.length,
			columnCount: this.columnMetadata.length,
			memorySize: JSON.stringify(this.cachedData).length,
			lastModified: new Date()
		}
	}

	async query(query: DataQuery): Promise<QueryResult> {
		let filteredData = [...this.cachedData]

		// Apply full-text search
		if (query.search?.query) {
			const lowerSearch = query.search.query.toLowerCase()
			filteredData = filteredData.filter((row) => {
				return Object.values(row).some((value) => {
					if (value == null) return false
					return String(value).toLowerCase().includes(lowerSearch)
				})
			})
		}

		// Apply sorting
		if (query.sort && query.sort.length > 0) {
			const sortSpec = query.sort[0] // Use first sort spec
			const sortBy = sortSpec.column
			const sortDirection = sortSpec.direction

			filteredData.sort((a, b) => {
				const aVal = a[sortBy as keyof VolcanoProperties]
				const bVal = b[sortBy as keyof VolcanoProperties]

				if (aVal == null && bVal == null) return 0
				if (aVal == null) return 1
				if (bVal == null) return -1

				let comparison = 0
				if (typeof aVal === 'string' && typeof bVal === 'string') {
					comparison = aVal.localeCompare(bVal)
				} else if (typeof aVal === 'number' && typeof bVal === 'number') {
					comparison = aVal - bVal
				} else {
					comparison = String(aVal).localeCompare(String(bVal))
				}

				return sortDirection === 'desc' ? -comparison : comparison
			})
		}

		// Calculate pagination
		const totalCount = filteredData.length
		const offset = query.offset || 0
		const limit = query.limit || 100
		const rows = filteredData.slice(offset, offset + limit)

		return {
			rows: rows as unknown as DataRow[],
			totalCount,
			offset,
			limit,
			hasMore: offset + limit < totalCount,
			executionTime: 0,
			fromCache: true
		}
	}

	async count(_filters?: FilterGroup): Promise<number> {
		// Simple count - filtering not implemented yet
		return this.cachedData.length
	}

	async dispose(): Promise<void> {
		this.cachedData = []
		this.isInitializedFlag = false
	}
}

